import { InputType } from 'pages/checkout/components/common/Input';
import { DIRECTION } from 'pages/common/components/Autocomplete/types';

import { FormInputConfig, MultiSelectInputConfig, SingleSelectInputConfig } from '../Insurance/renderer-helpers';

import { ANXIETY_OPTIONS, ATTITUDE_OPTIONS, GOALS_OPTIONS, LAST_VISITED_OPTIONS, TREATMENTS_OPTIONS, URGENT_DETAILS_OPTIONS, URGENT_OPTIONS } from './survey-options';

const COMMON_AUTOCOMPLETE_CONFIG = {
  preferredDropDirection: DIRECTION.DOWN,
} as const;

type SingleSelectParams = Pick<SingleSelectInputConfig, 'condition' | 'value' | 'onSelect' | 'errorMessage'>;
type MultiSelectParams = Pick<MultiSelectInputConfig, 'condition' | 'value' | 'onSelect' | 'errorMessage'>;
type TextAreaParams = Pick<FormInputConfig, 'condition' | 'value' | 'onChange' | 'errorMessage'>;
type ToggleParams = Pick<FormInputConfig, 'value' | 'onChange' | 'errorMessage'>;

export const FORM_INPUT_CONFIGS = {
  getAttitude: ({ value, onSelect, errorMessage }: SingleSelectParams) => ({
    id: 'attitude',
    label: 'When it comes to my mouth wellness, I feel...',
    type: InputType.SINGLE_SELECT_DROPDOWN,
    options: ATTITUDE_OPTIONS,
    ...COMMON_AUTOCOMPLETE_CONFIG,
    value,
    onSelect,
    errorMessage,
  }) as FormInputConfig,

  getGoals: ({ value, onSelect, errorMessage }: MultiSelectParams) => ({
    id: 'goals',
    label: 'The goals I have for my mouth are...',
    type: InputType.MULTI_SELECT_DROPDOWN,
    options: GOALS_OPTIONS,
    ...COMMON_AUTOCOMPLETE_CONFIG,
    value,
    onSelect,
    errorMessage,
  }) as FormInputConfig,

  getLastVisited: ({ value, onSelect, errorMessage }: SingleSelectParams) => ({
    id: 'lastVisited',
    label: 'When was your last visit to the dentist?',
    type: InputType.SINGLE_SELECT_DROPDOWN,
    options: LAST_VISITED_OPTIONS,
    ...COMMON_AUTOCOMPLETE_CONFIG,
    value,
    onSelect,
    errorMessage,
  }) as FormInputConfig,

  getAnxiety: ({ value, onSelect, errorMessage }: SingleSelectParams) => ({
    id: 'anxiety',
    label: 'How nervous do you feel about visiting the dentist?',
    type: InputType.SINGLE_SELECT_DROPDOWN,
    options: ANXIETY_OPTIONS,
    ...COMMON_AUTOCOMPLETE_CONFIG,
    value,
    onSelect,
    errorMessage,
  }),

  getUrgent: ({ value, onChange, errorMessage }: ToggleParams) => ({
    id: 'urgent',
    label: 'Do you have a dental emergency?',
    type: InputType.TOGGLE,
    options: URGENT_OPTIONS,
    ...COMMON_AUTOCOMPLETE_CONFIG,
    value,
    onChange,
    errorMessage,
  }) as FormInputConfig,

  getTreatments: ({ condition, value, onSelect, errorMessage }: MultiSelectParams) => ({
    id: 'treatments',
    label: 'Do you have any specific concerns or treatments you are seeking?',
    type: InputType.MULTI_SELECT_DROPDOWN,
    options: TREATMENTS_OPTIONS,
    ...COMMON_AUTOCOMPLETE_CONFIG,
    condition,
    value,
    onSelect,
    errorMessage,
  }) as FormInputConfig,

  getTreatmentsOther: ({ condition, value, onChange, errorMessage }: TextAreaParams) => ({
    id: 'treatments-other',
    label: 'Please describe your specific concerns or treatments',
    type: InputType.TEXTAREA,
    condition,
    value,
    onChange,
    errorMessage,
  }) as FormInputConfig,

  getUrgentDetails: ({ condition, value, onSelect, errorMessage }: SingleSelectParams) => ({
    id: 'urgent-details',
    label: 'What is the nature of your dental emergency?',
    type: InputType.SINGLE_SELECT_DROPDOWN,
    options: URGENT_DETAILS_OPTIONS,
    ...COMMON_AUTOCOMPLETE_CONFIG,
    condition,
    value,
    onSelect,
    errorMessage,
  }) as FormInputConfig,

  getUrgentDetailsOther: ({ condition, value, onChange, errorMessage }: TextAreaParams) => ({
    id: 'urgent-details-other',
    label: 'Please describe your dental emergency',
    type: InputType.TEXTAREA,
    condition,
    value,
    onChange,
    errorMessage,
  }) as FormInputConfig,
} as const;
